"use client"

import { useCollectionStatitstic } from "@/app/collections/statistic/use-floor"
import { Image } from "@/app/components/image"
import { CryptoAndUsdPrices } from "@/app/price"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { type EventContext, Flow } from "@/instrumentation/logger/types"
import * as amplitude from "@amplitude/analytics-browser"
import { joinAddress } from "@anaconda/core/blockchain/address"
import type { CollectionId } from "@anaconda/core/nft-collection/types"
import { cn } from "@rarible_int/anaconda_ui_components"
import { useMolecule } from "bunshi/react"
import { useEffect, useState } from "react"

// Helper function to get collection-specific background colors
function getCollectionBackground(collectionName: string): string {
  const name = collectionName.toLowerCase()

  // VeeFriends collections with specific colors based on their images
  if (name.includes("series 1")) {
    return "linear-gradient(135deg, #22d3ee 0%, #a78bfa 100%)" // cyan to purple
  }
  if (name.includes("series 2")) {
    return "linear-gradient(135deg, #a8834a 0%, #d4ba7e 100%)" // gold gradient
  }
  if (name.includes("iconics")) {
    return "linear-gradient(135deg, #a855f7 0%, #ec4899 100%)" // purple to pink
  }
  if (name.includes("mini drops")) {
    return "linear-gradient(135deg, #7c3aed 0%, #eab308 100%)" // purple to yellow
  }
  if (name.includes("book games")) {
    return "linear-gradient(135deg, #3f3f46 0%, #18181b 100%)" // deep charcoal gradient
  }

  // Default gradient for other collections
  return "linear-gradient(135deg, #52525b 0%, #3f3f46 100%)" // neutral dark gray
}

interface Collection {
  _id: string
  contractAddress: string
  blockchain: string
  name: string
  description?: string
  collectionImage?: string
  status: string
  currentVersion: number
  createdAt: string
  updatedAt: string
}

interface CollectionsSectionProps {
  title: string
  description?: string
  layout?: "grid" | "list"
  columns?: 2 | 3 | 4
  selectedCollections?: Array<{
    id: string
    blockchain: string
    address: string
  }>
  collections?: Collection[] // Collections data fetched from API
}

interface CollectionsSectionComponentProps {
  props: CollectionsSectionProps
  styles?: {
    backgroundColor?: string
    textColor?: string
    padding?: string
    margin?: string
  }
  merchantBlockchain?: string // Fallback blockchain for collections
}

export function CollectionsSection({
  props,
  styles,
  merchantBlockchain,
}: CollectionsSectionComponentProps) {
  const logger = useMolecule(LoggerMolecule)

  const {
    title,
    description,
    layout = "grid",
    columns = 4,
    collections = [],
  } = props

  useEffect(() => {
    const collectionsContext: EventContext = {
      sectionType: "collections",
      layout,
      columns: columns.toString(),
      collectionsCount: collections.length.toString(),
      hasTitle: (!!title).toString(),
      hasDescription: (!!description).toString(),
      merchantBlockchain: merchantBlockchain || "ethereum",
      flow: Flow.BrandPage,
    }

    logger?.api.pushEvent(
      "Brand collections section viewed",
      collectionsContext,
    )
    amplitude.track("Brand collections section viewed", collectionsContext)
  }, [
    title,
    description,
    layout,
    columns,
    collections.length,
    merchantBlockchain,
    logger,
  ])

  return (
    <div
      style={{
        color: styles?.textColor,
        padding: styles?.padding,
        margin: styles?.margin,
      }}
    >
      {/* Collections Grid */}
      <CollectionsLayout
        collections={collections}
        layout={layout}
        columns={columns}
        merchantBlockchain={merchantBlockchain}
      />

      {/* Empty State */}
      {collections.length === 0 && (
        <div className="py-12 text-center opacity-50">
          <p>No collections to display</p>
        </div>
      )}
    </div>
  )
}

function CollectionsLayout({
  collections,
  layout,
  columns,
  merchantBlockchain,
}: {
  collections: Collection[]
  layout: "grid" | "list"
  columns: number
  merchantBlockchain?: string
}) {
  const getGridColumns = () => {
    if (layout === "list") {
      return "grid-cols-1 gap-4"
    }

    switch (columns) {
      case 2:
        return "grid-cols-1 lg:grid-cols-2"
      case 3:
        return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
      case 4:
        return "grid-cols-1 sm:grid-cols-2 xl:grid-cols-4"
      default:
        return "grid-cols-1 sm:grid-cols-2 xl:grid-cols-4"
    }
  }

  return (
    <div className={`grid gap-6 ${getGridColumns()}`}>
      {collections.map((collection) => (
        <CollectionCard
          key={collection._id}
          collection={collection}
          merchantBlockchain={merchantBlockchain}
          layout={layout}
        />
      ))}
    </div>
  )
}

function CollectionCardWrapper({
  children,
  layout,
}: {
  children: React.ReactNode
  layout: "grid" | "list"
}) {
  return (
    <div className={cn(layout === "grid" ? "aspect-video w-full" : "w-full")}>
      {children}
    </div>
  )
}

function ProgressiveBlurMask() {
  return (
    <>
      <div
        className="pointer-events-none absolute inset-0 z-20"
        style={{
          backdropFilter: "blur(6.0px)",
          maskImage:
            "linear-gradient(to top, black 0%, black 11%, transparent 35%)",
          WebkitMaskImage:
            "linear-gradient(to top, black 0%, black 11%, transparent 35%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 z-20"
        style={{
          backdropFilter: "blur(10.0px)",
          maskImage: "linear-gradient(to top, black 0%, transparent 25%)",
          WebkitMaskImage: "linear-gradient(to top, black 0%, transparent 25%)",
        }}
      />

      <div
        className="pointer-events-none absolute inset-0 z-20"
        style={{
          backdropFilter: "blur(6.0px)",
          maskImage:
            "linear-gradient(to bottom, black 0%, black 11%, transparent 35%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, black 0%, black 11%, transparent 35%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 z-20"
        style={{
          backdropFilter: "blur(10.0px)",
          maskImage: "linear-gradient(to bottom, black 0%, transparent 25%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, black 0%, transparent 25%)",
        }}
      />
    </>
  )
}

function CollectionCard({
  collection,
  merchantBlockchain,
  layout,
}: {
  collection: Collection
  merchantBlockchain?: string
  layout: "grid" | "list"
}) {
  const logger = useMolecule(LoggerMolecule)

  // Only fetch stats if we have a valid collection
  const hasValidCollection = Boolean(collection.contractAddress?.trim())

  const handleCollectionClick = () => {
    const collectionContext: EventContext = {
      collectionId: collection._id,
      collectionName: collection.name,
      contractAddress: collection.contractAddress,
      blockchain: collection.blockchain,
      layout,
      merchantBlockchain: merchantBlockchain || "ethereum",
      flow: Flow.BrandPage,
      sectionType: "collections",
    }

    logger?.api.pushEvent("Brand collection clicked", collectionContext)
    amplitude.track("Brand collection clicked", collectionContext)
  }

  // State for dynamic background color - set based on collection name
  const [backgroundStyle, setBackgroundStyle] = useState<React.CSSProperties>({
    background: getCollectionBackground(collection.name),
  })

  // Update background when collection changes
  useEffect(() => {
    setBackgroundStyle({
      background: getCollectionBackground(collection.name),
    })
  }, [collection.name])

  // Construct proper CollectionId with blockchain prefix
  const getCollectionId = (): CollectionId => {
    if (!collection.contractAddress) return "" as CollectionId

    // If already has blockchain prefix (contains ":"), use as-is
    if (collection.contractAddress.includes(":")) {
      return collection.contractAddress as CollectionId
    }

    // Otherwise, add blockchain prefix
    const blockchain =
      collection.blockchain?.toUpperCase() ||
      merchantBlockchain?.toUpperCase() ||
      "ETHEREUM"
    return joinAddress(blockchain, collection.contractAddress) as CollectionId
  }

  const collectionId = getCollectionId()
  const {
    data: collectionStats,
    error,
    isLoading,
  } = useCollectionStatitstic(collectionId)

  // Build URL for blockchain/collections/collectionId
  const getCollectionUrl = () => {
    if (collection.contractAddress) {
      const blockchain =
        collection.blockchain?.toLowerCase() ||
        merchantBlockchain?.toLowerCase() ||
        "ethereum" // fallback to merchant blockchain, then ethereum
      return `/${blockchain}/collections/${collection.contractAddress}`
    }
    return "#"
  }

  const href = getCollectionUrl()

  if (layout === "list") {
    return (
      <CollectionCardWrapper layout={layout}>
        <a
          href={href}
          onClick={handleCollectionClick}
          className={cn(
            "group flex items-center gap-4 rounded-lg border border-white/10 p-4 transition-all hover:border-white/20",
            "relative overflow-hidden",
          )}
        >
          {/* Collection Image */}
          <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg">
            <Image
              containerClassName="transition-transform group-hover:scale-105"
              className="object-cover"
              imageSrc={collection.collectionImage}
              alt={collection.name}
            />
          </div>

          {/* Collection Info */}
          <div className="min-w-0 flex-1">
            <h6 className="truncate font-headline text-base md:text-lg">
              {collection.name}
            </h6>
            {collection.description && (
              <p className="mt-1 truncate text-sm opacity-70">
                {collection.description}
              </p>
            )}
          </div>

          {/* Stats */}
          <div className="flex flex-col items-end gap-1 text-right">
            {hasValidCollection && collectionStats?.floor && (
              <CryptoAndUsdPrices
                className="items-end"
                amount={collectionStats.floor.amount}
                currency={collectionStats.floor.currency}
              />
            )}
          </div>
        </a>
      </CollectionCardWrapper>
    )
  }

  return (
    <CollectionCardWrapper layout={layout}>
      <a
        href={href}
        onClick={handleCollectionClick}
        className={cn(
          "group flex-shrink-0 rounded-2xl p-6 transition-all hover:scale-105",
          "relative flex h-full w-full overflow-hidden",
        )}
        style={backgroundStyle}
      >
        {/* Content Container */}
        <div className="relative z-10 flex h-full w-full">
          {/* Left Side - Text Content */}
          <div className="flex w-3/5 flex-col justify-between">
            {/* Title - Top Left */}
            <h6 className="max-w-[50%] font-headline text-l text-white leading-tight md:text-lg">
              {collection.name}
            </h6>

            {/* Price - Bottom Left */}
            <div className="flex flex-col gap-[2px]">
              {hasValidCollection && collectionStats?.floor && (
                <CryptoAndUsdPrices
                  className="items-start"
                  amount={collectionStats.floor.amount}
                  currency={collectionStats.floor.currency}
                />
              )}
            </div>
          </div>

          {/* Right Side - Image */}
          <div className="flex w-2/5 items-center justify-end">
            <div className="relative aspect-[3/4] w-full max-w-56 sm:max-w-32 md:max-w-36 lg:max-w-40 xl:max-w-44">
              <div className="h-full w-full rounded-xl shadow-lg">
                <Image
                  containerClassName="h-full w-full rounded-lg overflow-hidden"
                  className="object-cover"
                  imageSrc={collection.collectionImage}
                  alt={collection.name}
                />
              </div>
            </div>
          </div>
        </div>
      </a>
    </CollectionCardWrapper>
  )
}
